// Copyright (c) Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.

import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  inject,
} from '@angular/core';

import { MatExpansionModule } from '@angular/material/expansion';
import { ItemsComponent } from '../items/items.component';
import { OptionsComponent } from '../options/options.component';
import { Checklist } from '../checklist';
import { LfChecklistService } from '../lf-checklist.service';

export interface LfChecklistProviders {
  checklistService: LfChecklistService;
}

@Component({
  selector: 'lf-checklist',
  templateUrl: './lf-checklist.component.html',
  styleUrls: ['./lf-checklist.component.css'],
  standalone: true,
  imports: [MatExpansionModule, ItemsComponent, OptionsComponent],
})
export class LfChecklistComponent {
  /**@internal */
  private ref = inject(ChangeDetectorRef);

  @Input() action_button_text: string | undefined;

  @Input() cancel_button_text: string | undefined;

  @Output() checklistChanged: EventEmitter<Checklist[]> = new EventEmitter();

  @Output() actionClick: EventEmitter<Checklist[]> = new EventEmitter();

  @Output() cancelClick: EventEmitter<Checklist[]> = new EventEmitter();

  /** @internal */
  @ViewChildren(ItemsComponent) private itemsComponents!: QueryList<ItemsComponent>;

  /** @internal */
  checklists: Checklist[] = [];

  @Input() initAsync = async (providers: LfChecklistProviders): Promise<void> => {
    return new Promise((resolve) => {
      requestAnimationFrame(async () => {
        this.checklists = await providers.checklistService.loadChecklistsAsync();
        this.itemsComponents?.forEach((component) => component.refreshChecklistItems());
        this.checklistChanged.emit(this.checklists);
        this.ref.markForCheck();
        resolve();
      });
    });
  };

  @Input() forceValidation = (): boolean => {
    let isValid = true;
    this.itemsComponents.forEach((component) => {
      if (!component.forceValidation()) {
        isValid = false;
      }
    });
    return isValid;
  };

  /** @internal */
  onActionButtonClicked(): void {
    this.actionClick.emit(this.checklists);
  }

  /** @internal */
  onCancelButtonClicked(): void {
    this.cancelClick.emit(this.checklists);
  }

  /** @internal */
  emitChecklistChanges(): void {
    this.checklistChanged.emit(this.checklists);
  }
}
